import { useQuery } from '@tanstack/react-query';
import type { IntegrationMessageStatus } from '@his/shared';
import { get } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Badge } from '@/components/ui/primitives';
import { relative } from '@/lib/utils';

interface NphiesStatus {
  lastMessage: { id: string; operation: string; status: IntegrationMessageStatus; errorMessage: string | null; createdAt: string; nextAttemptAt: string | null } | null;
}

const LABEL: Record<IntegrationMessageStatus, { text: string; variant: 'success' | 'warning' | 'destructive' | 'secondary' | 'default' }> = {
  queued: { text: 'Waiting for internet', variant: 'warning' },
  sending: { text: 'Sending', variant: 'default' },
  succeeded: { text: 'Sent', variant: 'success' },
  rejected: { text: 'Rejected', variant: 'destructive' },
  failed: { text: 'Failed', variant: 'destructive' },
  offline: { text: 'Not sent — offline', variant: 'warning' },
  cancelled: { text: 'Cancelled', variant: 'secondary' },
};

export function NphiesStatusLabel({ last }: { last: { status: string; errorMessage: string | null; createdAt: string } | null | undefined }) {
  const { hasModule } = useAuth();
  if (!hasModule('nphies') || !last) return null;
  const label = LABEL[last.status as IntegrationMessageStatus] ?? { text: last.status, variant: 'secondary' as const };
  return (
    <span title={last.errorMessage ?? undefined}>
      <Badge variant={label.variant}>NPHIES: {label.text}</Badge>
      <span className="ms-1.5 text-xs text-muted-foreground">{relative(last.createdAt)}</span>
    </span>
  );
}

export function NphiesStatusBadge({ entityType, entityId }: { entityType: 'claim' | 'pre_authorization' | 'patient_coverage'; entityId: string }) {
  const { hasModule, can } = useAuth();
  const enabled = hasModule('nphies') && can('nphies.messages.read', 'claim.read', 'preauth.manage');
  const status = useQuery({
    queryKey: ['nphies-status', entityType, entityId],
    queryFn: () => get<NphiesStatus>(`/integrations/nphies/status/${entityType}/${entityId}`),
    enabled,
    staleTime: 30_000,
  });
  const last = status.data?.lastMessage;
  if (!enabled || !last) return null;
  const label = LABEL[last.status];
  return (
    <span title={last.errorMessage ?? undefined}>
      <Badge variant={label.variant}>NPHIES: {label.text}</Badge>
      <span className="ms-1.5 text-xs text-muted-foreground">{relative(last.createdAt)}</span>
    </span>
  );
}
